/**
 * DealRoomViewPage — public, read-only deal-room view for the two
 * external parties (investor + startup/founder). Opened via a signed
 * link the admin shares: /deal-room/view?token=...
 *
 * No app login required — the token in the query string authorizes a
 * single room, scoped to the party's tier (investors) or the whole room
 * (founders). All data comes from GET /deal-room-view.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FileText,
  Lock,
  Loader2,
  ShieldCheck,
  Download,
  Send,
  AlertCircle,
} from 'lucide-react';
import dealRoomService from '../services/dealRoomService';
import DealStrategyView, { DealStrategyJson } from '../components/DealStrategyView';

interface ViewDoc {
  id: number;
  file_name: string;
  file_type?: string | null;
  access_tier?: string | null;
  file_size_bytes?: number | null;
  version?: number;
  locked?: boolean;
}

interface ViewMessage {
  id: number;
  sender_type?: string | null;
  sender_name?: string | null;
  body: string;
  created_at?: string | null;
}

interface ViewPayload {
  room: {
    id: number;
    room_name: string;
    status?: string | null;
    nda_required: boolean;
    nda_template_url?: string | null;
  };
  party: 'investor' | 'founder';
  investor_id?: number | null;
  tier?: string | null;
  nda_signed: boolean;
  strategy?: DealStrategyJson | null;
  documents: ViewDoc[];
  messages: ViewMessage[];
}

const fmtSize = (n?: number | null) => {
  if (!n) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} MB`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} KB`;
  return `${n} B`;
};

const tierChip = (t?: string | null) => {
  const map: Record<string, string> = {
    basic: 'bg-gray-100 text-gray-700',
    qualified: 'bg-blue-50 text-blue-700',
    lead: 'bg-purple-50 text-purple-700',
  };
  return map[t || 'basic'] || 'bg-gray-100 text-gray-700';
};

const DealRoomViewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ViewPayload | null>(null);
  const [signing, setSigning] = useState(false);
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setError('Missing access token. Use the link from your invitation email.');
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await dealRoomService.fetchView(token);
    setLoading(false);
    if (res.error || !res.data) {
      setError(res.error || 'This deal-room link is invalid or has expired.');
      return;
    }
    setError(null);
    setData(res.data as ViewPayload);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSignNda = async () => {
    setSigning(true);
    const res = await dealRoomService.signNdaView(token);
    setSigning(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('NDA accepted — documents unlocked.');
    await load();
  };

  const handleDownload = (doc: ViewDoc) => {
    if (doc.locked) {
      toast.error('Sign the NDA to download this document.');
      return;
    }
    // Direct browser navigation to the token-authorized download URL.
    window.open(dealRoomService.viewDownloadUrl(doc.id, token), '_blank');
  };

  const handleSend = async () => {
    if (!msgBody.trim()) return;
    setSending(true);
    const res = await dealRoomService.postViewMessage(token, msgBody.trim());
    setSending(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    setMsgBody('');
    await load();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-600">
        <Loader2 className="w-6 h-6 mr-3 animate-spin text-blue-600" />
        Loading deal room…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 mb-1">
            Can't open this deal room
          </h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const ndaGate = data.room.nda_required && !data.nda_signed && data.party === 'investor';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{data.room.room_name}</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Read-only deal room ·{' '}
                {data.party === 'investor' ? 'Investor view' : 'Company view'}
                {data.tier ? ` · ${data.tier} access` : ''}
              </p>
            </div>
            <span className="text-xs text-gray-400">VentureOS</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {ndaGate && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900">NDA required</p>
                <p className="text-sm text-amber-800 mt-1">
                  This room requires you to accept an NDA before downloading
                  documents.
                  {data.room.nda_template_url && (
                    <>
                      {' '}
                      <a
                        href={data.room.nda_template_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Review the NDA
                      </a>
                      .
                    </>
                  )}
                </p>
                <button
                  onClick={handleSignNda}
                  disabled={signing}
                  className="mt-3 inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  {signing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 mr-2" />
                  )}
                  Accept NDA
                </button>
              </div>
            </div>
          </div>
        )}

        {data.strategy && (
          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-2">
              Proposed deal strategy
            </h2>
            <DealStrategyView strategy={data.strategy} />
          </section>
        )}

        <section className="bg-white border border-gray-200 rounded-xl">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Documents
            </h2>
          </div>
          {data.documents.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-500">
              No documents available to you yet.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.documents.map((doc) => (
                <li
                  key={doc.id}
                  className="px-5 py-3 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {doc.file_name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {doc.access_tier && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${tierChip(
                            doc.access_tier
                          )}`}
                        >
                          {doc.access_tier}
                        </span>
                      )}
                      {doc.file_size_bytes ? (
                        <span className="text-[10px] text-gray-400">
                          {fmtSize(doc.file_size_bytes)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(doc)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium ${
                      doc.locked
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {doc.locked ? (
                      <>
                        <Lock className="w-3 h-3 mr-1" /> Locked
                      </>
                    ) : (
                      <>
                        <Download className="w-3 h-3 mr-1" /> Download
                      </>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white border border-gray-200 rounded-xl">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">Messages</h2>
          </div>
          <div className="px-5 py-4 space-y-3 max-h-80 overflow-y-auto">
            {data.messages.length === 0 ? (
              <p className="text-sm text-gray-500">No messages yet.</p>
            ) : (
              data.messages.map((m) => (
                <div key={m.id} className="text-sm">
                  <span className="font-medium text-gray-800 capitalize">
                    {m.sender_name || m.sender_type || 'User'}
                  </span>
                  <span className="text-gray-400 text-xs ml-2">
                    {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                  </span>
                  <p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{m.body}</p>
                </div>
              ))
            )}
          </div>
          <div className="px-5 py-3 border-t border-gray-100 flex gap-2">
            <input
              value={msgBody}
              onChange={(e) => setMsgBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Send a message to the team…"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={handleSend}
              disabled={sending || !msgBody.trim()}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default DealRoomViewPage;
